import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';

const BLANK = { name: '', breed: '', age: '', gender: 'cow', color: '', weight: '', healthStatus: 'healthy', adoptionFee: 1100, description: '', story: '', tagNumber: '', milkProduction: '' };
const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function ManageCows() {
  const [cows, setCows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [imageFile, setImageFile] = useState(null);

  const fetchCows = () => {
    setLoading(true);
    API.get('/cows').then(r => { setCows(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(fetchCows, []);

  const openAdd = () => { setForm(BLANK); setEditing(null); setShowForm(true); };
  const openEdit = (cow) => { setForm(cow); setEditing(cow._id); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name || !form.breed) return toast.error('Name and breed are required');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (imageFile) fd.append('image', imageFile);
      if (editing) {
        await API.put(`/cows/${editing}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Cow updated');
      } else {
        await API.post('/cows', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Cow added');
      }
      setShowForm(false); setImageFile(null); fetchCows();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this cow?')) return;
    await API.delete(`/cows/${id}`);
    toast.success('Cow removed');
    fetchCows();
  };

  const healthColor = h => ({ healthy: 'var(--green)', sick: '#c62828', recovering: '#f57f17', critical: '#b71c1c' }[h] || '#888');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>🐂 Manage Cows</h2>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Cow</button>
      </div>

      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 28 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>{editing ? 'Edit Cow' : 'Add New Cow'}</h3>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Name *</label><input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Breed *</label><input className="form-input" value={form.breed} onChange={e => setForm(p => ({ ...p, breed: e.target.value }))} placeholder="e.g., Gir, Sahiwal" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Age (years)</label><input className="form-input" type="number" value={form.age} onChange={e => setForm(p => ({ ...p, age: e.target.value }))} /></div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-select" value={form.gender} onChange={e => setForm(p => ({ ...p, gender: e.target.value }))}>
                <option value="cow">Cow (Female)</option><option value="bull">Bull (Male)</option><option value="calf">Calf</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Health Status</label>
              <select className="form-select" value={form.healthStatus} onChange={e => setForm(p => ({ ...p, healthStatus: e.target.value }))}>
                <option value="healthy">Healthy</option><option value="sick">Sick</option><option value="recovering">Recovering</option><option value="critical">Critical</option>
              </select>
            </div>
            <div className="form-group"><label className="form-label">Adoption Fee (₹)</label><input className="form-input" type="number" value={form.adoptionFee} onChange={e => setForm(p => ({ ...p, adoptionFee: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Tag Number</label><input className="form-input" value={form.tagNumber} onChange={e => setForm(p => ({ ...p, tagNumber: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Color</label><input className="form-input" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} /></div>
          </div>
          <div className="form-group"><label className="form-label">Story (for adoption page)</label><textarea className="form-input" rows={3} value={form.story} onChange={e => setForm(p => ({ ...p, story: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Cow Photo</label><input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} /></div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave}>Save</button>
            <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="loader"><div className="spinner" /></div> : (
        <div className="admin-table">
          <table>
            <thead><tr><th>Photo</th><th>Name</th><th>Breed</th><th>Age</th><th>Health</th><th>Adopted</th><th>Adoption Fee</th><th>Actions</th></tr></thead>
            <tbody>
              {cows.map(cow => (
                <tr key={cow._id}>
                  <td>{cow.image ? <img src={`${API_BASE}${cow.image}`} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} /> : '🐂'}</td>
                  <td style={{ fontWeight: 600 }}>{cow.name}</td>
                  <td>{cow.breed}</td>
                  <td>{cow.age || '—'} yrs</td>
                  <td><span style={{ color: healthColor(cow.healthStatus), fontWeight: 600 }}>{cow.healthStatus}</span></td>
                  <td>{cow.isAdopted ? '✅' : '—'}</td>
                  <td>₹{(cow.adoptionFee || 1100).toLocaleString('en-IN')}</td>
                  <td>
                    <button onClick={() => openEdit(cow)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--saffron)', marginRight: 8, fontSize: '1.1rem' }}>✏️</button>
                    <button onClick={() => handleDelete(cow._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: '1.1rem' }}>🗑️</button>
                  </td>
                </tr>
              ))}
              {cows.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No cows added yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
